import React, { useEffect } from 'react';

import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import {
  DocumentTableDocument,
  DocumentTableQuery,
  SaveDocumentInput,
  SaveDocumentMutation,
  useSaveDocumentFormQuery,
  useSaveDocumentMutation
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  apiFileId: string;
  folderId: string;
  id: string;
  projectId: string;
  title: string;
};

const initialState = {
  apiFileId: '',
  folderId: '',
  id: '',
  projectId: '',
  title: ''
};

export function useFormState(id: string, projectId: string, folderId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      apiFileId: {
        label: 'Api file id',
        type: 'text'
      },
      folderId: {
        label: 'Folder id',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      title: {
        label: 'Title',
        type: 'text'
      }
    }
  });



  useEffect(() => {
    if (
      projectId !== ''
      && formState.projectId === ''
    ) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          projectId
        }
      });

    }
    () => {};
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    formState.projectId,
    projectId
  ]);

  useEffect(() => {
    if (
      projectId !== ''
      && formState.projectId === ''
      && folderId !== ''
      && formState.folderId === ''
    ) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          folderId,
          projectId
        }
      });

    }
    () => {};

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    formState.projectId,
    folderId,
    formState.folderId,
    projectId
  ]);


  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (
      !_.isNil(data.id)
      && data.id !== ''
      && !loading
      && called
      && formState.id === ''
      && formState.title === ''
    ) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }

  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);


  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveDocument() {
  const [
    fnMutate,
    tuple
  ] = useSaveDocumentMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveDocumentInput) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          // ...gqlCacheUtil.genUpdateQueryMap<FolderListSectionQuery, SaveDocumentMutation>(FolderListSectionDocument, (prevState, { mutationResult }) => {
          //   if (!mutationResult.data?.saveDocument || !prevState.project) {
          //     return {
          //       ...prevState
          //     };
          //   }
          //   const incomingFolderId = mutationResult.data.saveDocument.folder?.id ?? '';


          //   const updatedFolders = prevState.project?.folders.map((folder) => {
          //     const document = folder.documents.find((doc) => doc.id === mutationResult.data?.saveDocument?.id);
          //     if (!document && folder.id === incomingFolderId) {
          //       return {
          //         ...folder,
          //         fileCount: folder.fileCount + 1
          //       };
          //     }
          //     if (folder.id === incomingFolderId) {
          //       return folder;
          //     }

          //     if (document && folder.id !== incomingFolderId) {
          //       return {
          //         ...folder,
          //         fileCount: folder.fileCount - 1
          //       };
          //     }
          //     return folder;
          //   });



          //   return {
          //     ...prevState,
          //     project: {
          //       ...prevState.project,
          //       folders: [...updatedFolders]
          //     }
          //   };
          // }),
          ...gqlCacheUtil.genUpdateQueryMap<DocumentTableQuery, SaveDocumentMutation>(DocumentTableDocument, (prevState, { mutationResult }) => {
            if (!prevState.project || !mutationResult.data?.saveDocument) {
              return prevState;
            }
            const existingDocuments = prevState.project.documents ?? [];
            const matchingDoc = prevState.project.documents.find((node) => node.id === mutationResult.data?.saveDocument?.id);

            if (matchingDoc) {
              return {
                ...prevState,
                project: {
                  ...prevState.project,
                  documents: _.compact(existingDocuments.map((node) => {
                    if (node.id === mutationResult.data?.saveDocument?.id) {
                      return {
                        ...node,
                        ...mutationResult.data.saveDocument
                      };
                    }
                  }))
                }
              };
            }

            return {
              ...prevState,
              project: {
                ...prevState.project,
                documents: [
                  ...prevState.project.documents,
                  mutationResult.data.saveDocument
                ]
              }
            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

// not overwriting exisitn project id becuase this is assumed to only be on projects if that changes will need to update
export interface IData extends Omit<IFormState, 'projectId'> {

}

export function useQuery(id: string) {
  const tuple = useSaveDocumentFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  const document = tuple.data?.document;

  return React.useMemo(
    () => {
      const data: IData = {
        apiFileId: document?.apiFile.id ?? '',
        folderId: document?.folder?.id ?? '',
        id: document?.id ?? '',
        title: document?.title ? document.title : document?.apiFile.fileName ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [
      document,
      tuple
    ]
  );
}
